import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiCheckCircle,
  FiCreditCard,
  FiDollarSign,
  FiPlus,
  FiTrendingDown,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeePageHeader
  from "../common/EmployeePageHeader";

import EmployeeModuleTabs
  from "../common/EmployeeModuleTabs";

import EmployeeSelector
  from "../common/EmployeeSelector";

import EmployeeConfirmModal
  from "../common/EmployeeConfirmModal";

import EmployeeAdvanceTable
  from "./EmployeeAdvanceTable";

import AddEditAdvanceModal
  from "./AddEditAdvanceModal";

import AdvanceRecoveryModal
  from "./AdvanceRecoveryModal";

import AdvanceDetailsDrawer
  from "./AdvanceDetailsDrawer";

import {
  formatEmployeeCurrency,
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeAdvance.css";


const INITIAL_FILTERS = {
  employeeId: "",
  status: "",
  search: "",
};


const INITIAL_PAGINATION = {
  page: 1,
  limit: 10,
  totalRecords: 0,
  totalPages: 1,
};


const INITIAL_SUMMARY = {
  totalAdvances: 0,
  totalAdvanceAmount: 0,
  totalRecoveredAmount: 0,
  totalBalanceAmount: 0,
  activeAdvances: 0,
  recoveredAdvances: 0,
  cancelledAdvances: 0,
};


const EmployeeAdvance = () => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    records,
    setRecords,
  ] =
    useState([]);


  const [
    employees,
    setEmployees,
  ] =
    useState([]);


  const [
    summary,
    setSummary,
  ] =
    useState({
      ...INITIAL_SUMMARY,
    });


  const [
    filters,
    setFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    appliedFilters,
    setAppliedFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    pagination,
    setPagination,
  ] =
    useState({
      ...INITIAL_PAGINATION,
    });


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    employeeLoading,
    setEmployeeLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const [
    selectedAdvance,
    setSelectedAdvance,
  ] =
    useState(null);


  const [
    addEditOpen,
    setAddEditOpen,
  ] =
    useState(false);


  const [
    recoveryOpen,
    setRecoveryOpen,
  ] =
    useState(false);


  const [
    detailsOpen,
    setDetailsOpen,
  ] =
    useState(false);


  const [
    cancelOpen,
    setCancelOpen,
  ] =
    useState(false);


  const [
    actionLoading,
    setActionLoading,
  ] =
    useState(false);


  /*
  |--------------------------------------------------------------------------
  | LOAD EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const loadEmployees =
    useCallback(
      async () => {

        try {

          setEmployeeLoading(
            true
          );


          const response =
            await api.post(
              config.getEmployees,
              {
                page: 1,
                limit: 500,
                status: "ACTIVE",
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employees."
            );

          }


          setEmployees(
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              []
          );

        } catch (
          requestError
        ) {

          console.error(
            "Employee advance employee list error:",
            requestError
          );


          setEmployees(
            []
          );

        } finally {

          setEmployeeLoading(
            false
          );

        }

      },
      [
        api,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD ADVANCES
  |--------------------------------------------------------------------------
  */

  const loadAdvances =
    useCallback(
      async () => {

        try {

          setLoading(
            true
          );


          setError(
            ""
          );


          const response =
            await api.post(
              config.getEmployeeAdvances,
              {
                page:
                  pagination.page,

                limit:
                  pagination.limit,

                employeeId:
                  appliedFilters.employeeId,

                status:
                  appliedFilters.status,

                search:
                  appliedFilters.search,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employee advances."
            );

          }


          const data =
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              [];


          setRecords(
            data
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              totalRecords:
                Number(
                  response
                    ?.pagination
                    ?.totalRecords ??
                  data.length
                ),

              totalPages:
                Math.max(
                  Number(
                    response
                      ?.pagination
                      ?.totalPages
                  ) ||
                  1,
                  1
                ),
            })
          );

        } catch (
          requestError
        ) {

          console.error(
            "Employee advance list error:",
            requestError
          );


          setRecords(
            []
          );


          setError(
            getEmployeeApiError(
              requestError,
              "Unable to load employee advances."
            )
          );

        } finally {

          setLoading(
            false
          );

        }

      },
      [
        api,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | SUMMARY
  |--------------------------------------------------------------------------
  */

  const loadSummary =
    useCallback(
      async () => {

        try {

          const response =
            await api.get(
              config.getEmployeeAdvanceSummary,
              {
                employeeId:
                  appliedFilters.employeeId,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            return;

          }


          const data =
            response?.data ||
            {};


          setSummary({
            totalAdvances:
              Number(
                data.totalAdvances ??
                data.totalRecords ??
                0
              ),

            totalAdvanceAmount:
              Number(
                data.totalAdvanceAmount ??
                data.advanceAmount ??
                0
              ),

            totalRecoveredAmount:
              Number(
                data.totalRecoveredAmount ??
                data.recoveredAmount ??
                0
              ),

            totalBalanceAmount:
              Number(
                data.totalBalanceAmount ??
                data.balanceAmount ??
                0
              ),

            activeAdvances:
              Number(
                data.activeAdvances ??
                data.activeCount ??
                0
              ),

            recoveredAdvances:
              Number(
                data.recoveredAdvances ??
                data.recoveredCount ??
                0
              ),

            cancelledAdvances:
              Number(
                data.cancelledAdvances ??
                data.cancelledCount ??
                0
              ),
          });

        } catch (
          requestError
        ) {

          console.error(
            "Employee advance summary error:",
            requestError
          );

        }

      },
      [
        api,
        appliedFilters.employeeId,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      loadEmployees();

    },
    [
      loadEmployees,
    ]
  );


  useEffect(
    () => {

      loadAdvances();
      loadSummary();

    },
    [
      loadAdvances,
      loadSummary,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const handleSearch =
    () => {

      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );


      setAppliedFilters({
        ...filters,

        search:
          String(
            filters.search ||
            ""
          ).trim(),
      });

    };


  /*
  |--------------------------------------------------------------------------
  | RESET
  |--------------------------------------------------------------------------
  */

  const handleReset =
    () => {

      setFilters({
        ...INITIAL_FILTERS,
      });


      setAppliedFilters({
        ...INITIAL_FILTERS,
      });


      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUCCESS
  |--------------------------------------------------------------------------
  */

  const handleSuccess =
    async () => {

      setAddEditOpen(
        false
      );

      setRecoveryOpen(
        false
      );

      setDetailsOpen(
        false
      );

      setCancelOpen(
        false
      );

      setSelectedAdvance(
        null
      );


      await Promise.all([
        loadAdvances(),
        loadSummary(),
      ]);

    };


  /*
  |--------------------------------------------------------------------------
  | CANCEL ADVANCE
  |--------------------------------------------------------------------------
  */

  const handleCancel =
    async (
      reason
    ) => {

      if (
        !selectedAdvance?._id
      ) {

        return;

      }


      try {

        setActionLoading(
          true
        );


        const response =
          await api.patch(
            config.cancelEmployeeAdvance(
              selectedAdvance._id
            ),
            {
              reason,
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to cancel advance."
          );

        }


        await handleSuccess();

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to cancel advance."
          )
        );

      } finally {

        setActionLoading(
          false
        );

      }

    };


  const summaryCards = [

    {
      label:
        "Total Advances",

      value:
        summary.totalAdvances,

      icon:
        FiCreditCard,

      type:
        "blue",
    },

    {
      label:
        "Advance Amount",

      value:
        formatEmployeeCurrency(
          summary.totalAdvanceAmount
        ),

      icon:
        FiDollarSign,

      type:
        "purple",
    },

    {
      label:
        "Recovered",

      value:
        formatEmployeeCurrency(
          summary.totalRecoveredAmount
        ),

      icon:
        FiCheckCircle,

      type:
        "green",
    },

    {
      label:
        "Outstanding",

      value:
        formatEmployeeCurrency(
          summary.totalBalanceAmount
        ),

      icon:
        FiTrendingDown,

      type:
        "orange",
    },

  ];


  return (

    <div className="employee-advance-page">

      <EmployeePageHeader
        title="Employee Advances"
        description="Manage employee advances, recovery transactions and outstanding balances."
        actionLabel="Add Advance"
        actionIcon={
          <FiPlus />
        }
        onAction={
          () => {

            setSelectedAdvance(
              null
            );

            setAddEditOpen(
              true
            );

          }
        }
        onRefresh={
          () =>
            Promise.all([
              loadAdvances(),
              loadSummary(),
            ])
        }
        loading={
          loading
        }
      />


      <EmployeeModuleTabs />


      <div className="employee-advance-summary-grid">

        {
          summaryCards.map(
            (
              card
            ) => {

              const Icon =
                card.icon;


              return (

                <article
                  key={
                    card.label
                  }
                  className={`employee-advance-summary-card employee-advance-summary-card--${card.type}`}
                >

                  <span className="employee-advance-summary-icon">

                    <Icon />

                  </span>


                  <div>

                    <small>
                      {card.label}
                    </small>

                    <strong>
                      {card.value}
                    </strong>

                  </div>

                </article>

              );

            }
          )
        }

      </div>


      <section className="employee-advance-filter-card">

        <div className="employee-advance-filter-grid">

          <EmployeeSelector
            employees={
              employees
            }
            value={
              filters.employeeId
            }
            onChange={
              (
                employeeId
              ) =>
                setFilters(
                  (
                    previous
                  ) => ({
                    ...previous,
                    employeeId,
                  })
                )
            }
            label="Employee"
            placeholder="All Employees"
            disabled={
              employeeLoading
            }
          />


          <div className="employee-advance-filter-field">

            <label>
              Status
            </label>

            <select
              value={
                filters.status
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      status:
                        event
                          .target
                          .value,
                    })
                  )
              }
            >

              <option value="">
                All Statuses
              </option>

              <option value="ACTIVE">
                Active
              </option>

              <option value="PARTIALLY_RECOVERED">
                Partially Recovered
              </option>

              <option value="RECOVERED">
                Recovered
              </option>

              <option value="CANCELLED">
                Cancelled
              </option>

            </select>

          </div>


          <div className="employee-advance-filter-field">

            <label>
              Search
            </label>

            <input
              type="text"
              value={
                filters.search
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      search:
                        event
                          .target
                          .value,
                    })
                  )
              }
              onKeyDown={
                (
                  event
                ) => {

                  if (
                    event.key ===
                    "Enter"
                  ) {

                    handleSearch();

                  }

                }
              }
              placeholder="Employee or reference..."
            />

          </div>


          <div className="employee-advance-filter-actions">

            <button
              type="button"
              className="employee-advance-apply-btn"
              onClick={
                handleSearch
              }
            >
              Apply
            </button>


            <button
              type="button"
              className="employee-advance-reset-btn"
              onClick={
                handleReset
              }
            >
              Reset
            </button>

          </div>

        </div>

      </section>


      {
        error
          ?
          (
            <div className="employee-advance-error">
              {error}
            </div>
          )
          :
          null
      }


      <EmployeeAdvanceTable
        records={
          records
        }
        loading={
          loading
        }
        page={
          pagination.page
        }
        limit={
          pagination.limit
        }
        totalRecords={
          pagination.totalRecords
        }
        totalPages={
          pagination.totalPages
        }
        onPageChange={
          (
            page
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page,
              })
            )
        }
        onLimitChange={
          (
            limit
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page: 1,
                limit,
              })
            )
        }
        onView={
          (
            advance
          ) => {

            setSelectedAdvance(
              advance
            );

            setDetailsOpen(
              true
            );

          }
        }
        onEdit={
          (
            advance
          ) => {

            setSelectedAdvance(
              advance
            );

            setAddEditOpen(
              true
            );

          }
        }
        onRecover={
          (
            advance
          ) => {

            setSelectedAdvance(
              advance
            );

            setRecoveryOpen(
              true
            );

          }
        }
        onCancel={
          (
            advance
          ) => {

            setSelectedAdvance(
              advance
            );

            setCancelOpen(
              true
            );

          }
        }
      />


      <AddEditAdvanceModal
        open={
          addEditOpen
        }
        advance={
          selectedAdvance
        }
        employees={
          employees
        }
        onClose={
          () => {

            setAddEditOpen(
              false
            );

            setSelectedAdvance(
              null
            );

          }
        }
        onSuccess={
          handleSuccess
        }
      />


      <AdvanceRecoveryModal
        open={
          recoveryOpen
        }
        advance={
          selectedAdvance
        }
        onClose={
          () => {

            setRecoveryOpen(
              false
            );

            setSelectedAdvance(
              null
            );

          }
        }
        onSuccess={
          handleSuccess
        }
      />


      <AdvanceDetailsDrawer
        open={
          detailsOpen
        }
        advance={
          selectedAdvance
        }
        onClose={
          () => {

            setDetailsOpen(
              false
            );

            setSelectedAdvance(
              null
            );

          }
        }
        onEdit={
          (
            advance
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedAdvance(
              advance
            );

            setAddEditOpen(
              true
            );

          }
        }
        onRecover={
          (
            advance
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedAdvance(
              advance
            );

            setRecoveryOpen(
              true
            );

          }
        }
        onCancel={
          (
            advance
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedAdvance(
              advance
            );

            setCancelOpen(
              true
            );

          }
        }
      />


      <EmployeeConfirmModal
        open={
          cancelOpen
        }
        title="Cancel Employee Advance"
        description="This advance will be marked as cancelled and cannot be recovered further."
        confirmLabel="Cancel Advance"
        requireReason
        reasonLabel="Cancellation Reason"
        reasonPlaceholder="Enter cancellation reason"
        danger
        loading={
          actionLoading
        }
        onClose={
          () => {

            setCancelOpen(
              false
            );

            setSelectedAdvance(
              null
            );

          }
        }
        onConfirm={
          handleCancel
        }
      />

    </div>

  );

};


export default EmployeeAdvance;